import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Wallet, Building2, Phone, AlertTriangle, Receipt, ChevronRight } from 'lucide-react';

import { listDebts, listOrders, type DebtStatus } from '@/services/orders';
import { PageHeader, Spinner, EmptyState } from '@/components/fields';
import { rub } from '@/lib/format';

const DEBT_BADGE: Record<DebtStatus, { label: string; cls: string }> = {
  UNPAID: { label: 'Не оплачен', cls: 'bg-amber-50 text-amber-700' },
  PARTIAL: { label: 'Частично', cls: 'bg-amber-50 text-amber-700' },
  OVERDUE: { label: 'Просрочен', cls: 'bg-red-50 text-red-700' },
  PAID: { label: 'Оплачен', cls: 'bg-emerald-50 text-emerald-700' },
};

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export default function Debts() {
  const { data, isLoading } = useQuery({
    queryKey: ['debts'],
    queryFn: listDebts,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
  const unpaid = useQuery({
    queryKey: ['orders', 'debtOnly'],
    queryFn: () => listOrders({ debtOnly: true, limit: 100 }),
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const rows = data?.results ?? [];
  const totals = data?.totals ?? { currentDebt: 0, overdueDebt: 0, totalOrdered: 0 };
  const unpaidOrders = unpaid.data?.results ?? [];

  return (
    <div>
      <PageHeader title="Долги" subtitle="Дебиторка по заведениям (офлайн-расчёты за товар)" />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <Kpi label="Текущий долг" value={rub(totals.currentDebt)} accent="amber" />
        <Kpi label="Просрочено" value={rub(totals.overdueDebt)} accent="red" />
        <Kpi label="Оборот всего" value={rub(totals.totalOrdered)} accent="ink" />
      </div>

      {isLoading ? (
        <Spinner label="Считаем долги…" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Wallet size={40} strokeWidth={1.4} />}
          title="Долгов нет"
          description="Здесь появятся заведения с непогашенной задолженностью по поставкам."
        />
      ) : (
        <div className="card divide-y divide-line">
          {rows.map((r) => (
            <Link
              key={r.establishmentId}
              to={`/customers/${r.establishmentId}`}
              className="px-4 py-3.5 flex items-center gap-3 hover:bg-paper transition"
            >
              <div className="w-9 h-9 rounded-lg bg-ink/5 text-ink/40 flex items-center justify-center shrink-0">
                <Building2 size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink truncate">{r.name}</div>
                <div className="text-xs text-ink/45 flex items-center gap-2 mt-0.5">
                  {r.phone && (
                    <span className="inline-flex items-center gap-1">
                      <Phone size={11} /> {r.phone}
                    </span>
                  )}
                  <span>{r.ordersCount} заказ(ов)</span>
                </div>
                {r.note && <div className="text-xs text-ink/50 mt-0.5 italic truncate">«{r.note}»</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="font-semibold text-amber-700">{rub(r.currentDebt)}</div>
                {r.overdueDebt > 0 && (
                  <div className="text-xs text-red-600 flex items-center justify-end gap-1 mt-0.5">
                    <AlertTriangle size={11} /> {rub(r.overdueDebt)} просроч.
                  </div>
                )}
                {r.creditLimit != null && (
                  <div className="text-[11px] text-ink/40 mt-0.5">лимит {rub(r.creditLimit)}</div>
                )}
              </div>
              <ChevronRight size={18} className="text-ink/25 shrink-0" />
            </Link>
          ))}
        </div>
      )}

      {unpaidOrders.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-2 text-ink/70">
            <Receipt size={16} />
            <h2 className="text-sm font-semibold">Неоплаченные заказы</h2>
            <span className="text-xs text-ink/40">{unpaidOrders.length}</span>
          </div>
          <div className="card divide-y divide-line">
            {unpaidOrders.map((o) => {
              const owed = Math.max(0, o.totalCost - o.paidAmount);
              const b = DEBT_BADGE[o.debtStatus] ?? DEBT_BADGE.UNPAID;
              return (
                <Link
                  key={o.id}
                  to={`/customers/${o.establishment.id}`}
                  className="px-4 py-3 flex items-center gap-3 hover:bg-paper transition"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-ink truncate">{o.establishment.name}</div>
                    <div className="text-xs text-ink/45 mt-0.5 flex items-center gap-2">
                      <span>{shortDate(o.createdAt)}</span>
                      <span>·</span>
                      <span>{o.items.length} поз.</span>
                      {o.dueDate && <span className="text-red-500/80">до {shortDate(o.dueDate)}</span>}
                    </div>
                  </div>
                  <span className={`badge ${b.cls} shrink-0`}>{b.label}</span>
                  <div className="text-right shrink-0 w-24">
                    <div className="font-semibold text-amber-700">{rub(owed)}</div>
                    {o.paidAmount > 0 && (
                      <div className="text-[11px] text-ink/40">из {rub(o.totalCost)}</div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: 'amber' | 'red' | 'ink';
}) {
  const color =
    accent === 'amber' ? 'text-amber-700' : accent === 'red' ? 'text-red-600' : 'text-ink';
  return (
    <div className="card p-4">
      <div className="text-xs text-ink/45">{label}</div>
      <div className={`text-xl font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
